import { apmBase } from '@elastic/apm-rum';
import { HttpTransportType, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import type { HubConnection } from '@microsoft/signalr';
import { useMemoize } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import type { IBetBuilderRecommendation } from '@/interface/IBetBuilderRecommendation';
import type { RogueModelsBetBuilderPreBuilt } from '@/interface/rogue';
import {
    formatBetBuilderRecommendation,
} from '@/libraries/recommendation/betBuilderRecommendationHelper';
import { waitForOnline } from '@/libraries/utils/networkStatus';
import { sleep } from '@/libraries/utils/utils';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';

type UpdateCallback = (data: IBetBuilderRecommendation) => void;
type DeleteCallback = () => void;

interface Subscriber {
    onUpdate: UpdateCallback;
    onDelete: DeleteCallback;
}

const connection = ref<HubConnection | null>(null);
const subscribers = new Map<string, Set<Subscriber>>();

let isStopping = false;

async function safeInvoke(method: string, key: string): Promise<void> {
    if (isStopping) return;
    if (connection.value?.state !== HubConnectionState.Connected) return;
    await connection.value.invoke(method, key);
}

const retryDelaysSeconds = [0, 2, 10, 30];
let retryCount = 0;

function getErrorMessage(err: unknown): string {
    return err instanceof Error ? err.message : String(err);
}

function isStartStoppedError(err: unknown): boolean {
    return getErrorMessage(err).includes('Failed to start the HttpConnection before stop() was called.');
}

function captureHubError(err: unknown) {
    const error = err instanceof Error ? err : new Error(String(err));
    error.message = `[BetBuilderHub] ${error.message}`;
    console.error(error);
    apmBase.captureError(error);
}

function handleSubscribeError(err: unknown) {
    if (connection.value?.state === HubConnectionState.Connected) {
        captureHubError(err);
        return;
    }
    console.warn('[BetBuilderHub] Subscribe canceled because the connection closed', err);
}

const startConnection = useMemoize(async () => {
    const connectionHub = connection.value ?? createConnection();
    connection.value = connectionHub;
    try {
        await connectionHub.start();
        retryCount = 0;
    } catch (err) {
        connection.value = null;
        if (isStartStoppedError(err)) {
            retryCount = 0;
            return;
        }
        if (!navigator.onLine) {
            retryCount = 0;
            startConnection.clear();
            await waitForOnline();
            await startConnection();
        } else if (retryCount < retryDelaysSeconds.length) {
            const delay = retryDelaysSeconds[retryCount++];
            startConnection.clear();
            await sleep(delay);
            await startConnection();
        } else {
            retryCount = 0;
            captureHubError(err);
        }
    } finally {
        startConnection.clear();
    }
});

function createConnection() {
    const { newOddsApiWsUrl } = storeToRefs(useFeatureToggleStore());
    const hubUrl = `${newOddsApiWsUrl.value}/hubs/recommendationHub`;
    const conn = new HubConnectionBuilder()
        .withUrl(hubUrl, {
            skipNegotiation: true,
            transport: HttpTransportType.WebSockets,
        })
        .withAutomaticReconnect()
        .configureLogging(__ENV_PROD__ ? LogLevel.Error : LogLevel.Warning)
        .build();

    conn.on('BetBuilderPreBuiltUpdated', (raw: RogueModelsBetBuilderPreBuilt) => {
        const data = formatBetBuilderRecommendation(raw);
        const { combinationKey } = data;
        subscribers.get(combinationKey)?.forEach(s => s.onUpdate(data));
    });

    conn.on('BetBuilderPreBuiltDeleted', (key: string) => {
        subscribers.get(key)?.forEach(s => s.onDelete());
    });

    conn.onreconnecting((err) => {
        console.warn('[BetBuilderHub] Connection lost, attempting to reconnect...', err);
    });

    conn.onreconnected(async () => {
        const keys = Array.from(subscribers.keys());
        await Promise.allSettled(keys.map(key => safeInvoke('SubscribeBetBuilderPreBuilt', key)));
    });

    conn.onclose(async (err) => {
        if (err) {
            console.error('[BetBuilderHub] Connection permanently closed with error', err);
        }
        connection.value = null;

        if (isStopping) {
            isStopping = false;
            return;
        }

        if (subscribers.size > 0) {
            try {
                await tryConnect();
                await Promise.allSettled(
                    Array.from(subscribers.keys()).map(key => safeInvoke('SubscribeBetBuilderPreBuilt', key)),
                );
            } catch (e) {
                console.error('[BetBuilderHub] Failed to reconnect after close', e);
            }
        }
    });

    return conn;
}

async function tryConnect() {
    const { isBetBuilderPrebuiltEnabled } = storeToRefs(useFeatureToggleStore());
    if (!isBetBuilderPrebuiltEnabled.value) return;
    if (connection.value !== null && connection.value.state !== HubConnectionState.Disconnected) return;

    await startConnection();
}

async function subscribe(key: string, callbacks: Subscriber) {
    await tryConnect();

    let set = subscribers.get(key);
    if (!set) {
        set = new Set();
        subscribers.set(key, set);
    }
    set.add(callbacks);

    try {
        await safeInvoke('SubscribeBetBuilderPreBuilt', key);
    } catch (err) {
        handleSubscribeError(err);
    }
}

async function unsubscribe(key: string, callbacks: Subscriber) {
    const set = subscribers.get(key);
    if (!set) return;

    set.delete(callbacks);
    if (set.size === 0) {
        subscribers.delete(key);
        try {
            await safeInvoke('UnsubscribeBetBuilderPreBuilt', key);
        } catch (_) { // NOSONAR
            // Ignored intentionally: if the connection is already closing or closed when
            // unsubscribe is called (e.g. during component unmount), the invocation will
            // be rejected with "Invocation canceled due to the underlying connection being
            // closed." This is expected and safe to ignore — the subscription will be
            // cleaned up on the server when the connection drops.
        }
    }

    if (subscribers.size === 0 && connection.value) {
        const currentConnection = connection.value;

        // Wait for 1 second to let unsubscribe complete before stopping connection,
        // to prevent unsubscribe then subscribe behavior
        await sleep(1);
        if (subscribers.size > 0 || connection.value !== currentConnection) return;

        isStopping = true;

        try {
            await currentConnection.stop();
        } catch (_) { // NOSONAR
            // Ignored intentionally: SignalR's stop() rejects when there are pending
            // invocations at the time of shutdown — it internally cancels them and
            // propagates the rejection back to the caller. Since we are intentionally
            // tearing down the connection, this error is expected and does not indicate
            // any data loss or incorrect state.
        }
        if (connection.value === currentConnection) {
            connection.value = null;
        }
    }
}

export function useBetBuilderHub() {
    return {
        subscribe,
        unsubscribe,
    };
}
